import {
    Article as ArticleIcon,
    Category as CategoryIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    Psychology as PsychologyIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    ThumbDown as ThumbDownIcon,
    ThumbUp as ThumbUpIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/GoogleAuthContext';
import { api } from '../services/api-client';

// Types
interface SourceArticle {
  articleId: string;
  articleTitle: string;
  articleText?: string;
  chunkText?: string;
  sentenceIds?: string[];
  loggedAt: any;
}

interface LLMAnalysis {
  classification: string;
  confidence: number;
  reasoning: string;
  model: string;
  analyzedAt: any;
}

interface GenericTerm {
  id: string;
  term: string;
  normalized: string;
  status: 'pending' | 'analyzed' | 'confirmed_generic' | 'confirmed_valid';
  sourceArticles: SourceArticle[];
  llmAnalysis?: LLMAnalysis;
  confirmedBy?: string;
  confirmedAt?: any;
  firstSeen: any;
  timesSeen: number;
  lastSeen: any;
}

interface ExpandedRows {
  [key: string]: boolean;
}

const statusColors: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  pending: 'warning',
  analyzed: 'info',
  confirmed_generic: 'error',
  confirmed_valid: 'success',
};

const classificationColors: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  generic_category: 'error',
  article_title: 'warning',
  valid_venue: 'success',
  unclear: 'default',
};

export default function GenericTerms() {
  const { user } = useAuth();
  const [terms, setTerms] = useState<GenericTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<ExpandedRows>({});
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    term: GenericTerm | null;
    action: 'generic' | 'valid' | null;
  }>({ open: false, term: null, action: null });
  const [updating, setUpdating] = useState(false);

  // Fetch terms from backend API
  const fetchTerms = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.getGenericTerms(filterStatus);
      setTerms(response.terms as GenericTerm[]);
    } catch (err: any) {
      console.error('Error fetching terms:', err);
      setError(err.message || 'Failed to fetch generic terms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTerms();
  }, [filterStatus]);

  const handleExpandClick = (termId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [termId]: !prev[termId],
    }));
  };

  const handleConfirmAction = (term: GenericTerm, action: 'generic' | 'valid') => {
    setConfirmDialog({ open: true, term, action });
  };

  const executeConfirmation = async () => {
    if (!confirmDialog.term || !confirmDialog.action || !user) return;

    setUpdating(true);
    try {
      const newStatus =
        confirmDialog.action === 'generic' ? 'confirmed_generic' : 'confirmed_valid';

      await api.updateGenericTerm(confirmDialog.term.id, newStatus);

      // Update local state
      setTerms((prev) =>
        prev.map((t) =>
          t.id === confirmDialog.term!.id
            ? {
                ...t,
                status: newStatus,
                confirmedBy: user.email,
                confirmedAt: new Date().toISOString(),
              }
            : t
        )
      );

      setConfirmDialog({ open: false, term: null, action: null });
    } catch (err: any) {
      console.error('Error updating term:', err);
      setError(err.message || 'Failed to update term');
    } finally {
      setUpdating(false);
    }
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    try {
      // Handle both Firestore Timestamp and ISO string
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleString();
      }
      if (typeof timestamp === 'string') {
        return new Date(timestamp).toLocaleString();
      }
      if (timestamp._seconds) {
        return new Date(timestamp._seconds * 1000).toLocaleString();
      }
      return 'Invalid date';
    } catch {
      return 'Invalid date';
    }
  };

  const filteredTerms = terms.filter((term) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        term.term.toLowerCase().includes(searchLower) ||
        term.sourceArticles?.some((sa) =>
          sa.articleTitle?.toLowerCase().includes(searchLower)
        )
      );
    }
    return true;
  });

  // Stats
  const stats = {
    total: terms.length,
    pending: terms.filter((t) => t.status === 'pending').length,
    analyzed: terms.filter((t) => t.status === 'analyzed').length,
    confirmedGeneric: terms.filter((t) => t.status === 'confirmed_generic').length,
    confirmedValid: terms.filter((t) => t.status === 'confirmed_valid').length,
  };

  if (loading && terms.length === 0) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Generic Terms Review
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4 }}>
          <CircularProgress />
          <Typography>Loading terms...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CategoryIcon color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4">Generic Terms Review</Typography>
            <Typography variant="body2" color="text.secondary">
              Review and confirm extracted terms as generic or valid venues
            </Typography>
          </Box>
        </Box>
        <Button startIcon={<RefreshIcon />} variant="outlined" onClick={fetchTerms}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4">{stats.total}</Typography>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ borderLeft: '4px solid orange' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4">{stats.pending}</Typography>
              <Typography variant="body2" color="text.secondary">
                Pending
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ borderLeft: '4px solid #2196f3' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4">{stats.analyzed}</Typography>
              <Typography variant="body2" color="text.secondary">
                Analyzed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ borderLeft: '4px solid #f44336' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4">{stats.confirmedGeneric}</Typography>
              <Typography variant="body2" color="text.secondary">
                Generic
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ borderLeft: '4px solid #4caf50' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4">{stats.confirmedValid}</Typography>
              <Typography variant="body2" color="text.secondary">
                Valid
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search terms or articles"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Filter by Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending (Not Analyzed)</MenuItem>
                  <MenuItem value="analyzed">Analyzed (Awaiting Review)</MenuItem>
                  <MenuItem value="confirmed_generic">Confirmed Generic</MenuItem>
                  <MenuItem value="confirmed_valid">Confirmed Valid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Terms Table */}
      <TableContainer component={Paper}>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Term</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>LLM Classification</TableCell>
              <TableCell>Confidence</TableCell>
              <TableCell>Times Seen</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTerms.map((term) => (
              <React.Fragment key={term.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleExpandClick(term.id)}>
                      {expandedRows[term.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {term.term}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={term.status.replace('_', ' ')}
                      size="small"
                      color={statusColors[term.status]}
                    />
                  </TableCell>
                  <TableCell>
                    {term.llmAnalysis ? (
                      <Chip
                        icon={<PsychologyIcon />}
                        label={term.llmAnalysis.classification.replace('_', ' ')}
                        size="small"
                        color={classificationColors[term.llmAnalysis.classification]}
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not analyzed
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {term.llmAnalysis ? (
                      <Typography variant="body2">
                        {(term.llmAnalysis.confidence * 100).toFixed(0)}%
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={term.timesSeen} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {term.status !== 'confirmed_generic' && term.status !== 'confirmed_valid' && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Confirm as Generic (filter out)">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleConfirmAction(term, 'generic')}
                          >
                            <ThumbDownIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Confirm as Valid (keep)">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleConfirmAction(term, 'valid')}
                          >
                            <ThumbUpIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    {(term.status === 'confirmed_generic' ||
                      term.status === 'confirmed_valid') && (
                      <Typography variant="caption" color="text.secondary">
                        {term.confirmedBy?.split('@')[0]}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 0 }}>
                    <Collapse in={expandedRows[term.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                          {/* LLM Analysis */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                              <PsychologyIcon
                                sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }}
                              />
                              LLM Analysis
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              {term.llmAnalysis ? (
                                <>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>Classification:</strong>{' '}
                                    {term.llmAnalysis.classification}
                                  </Typography>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>Confidence:</strong>{' '}
                                    {(term.llmAnalysis.confidence * 100).toFixed(0)}%
                                  </Typography>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>Reasoning:</strong>
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontStyle: 'italic' }}
                                  >
                                    {term.llmAnalysis.reasoning}
                                  </Typography>
                                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                    Model: {term.llmAnalysis.model} |{' '}
                                    {formatTimestamp(term.llmAnalysis.analyzedAt)}
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  Not yet analyzed by LLM. Run the batch analyzer.
                                </Typography>
                              )}
                            </Paper>
                          </Grid>

                          {/* Source Articles */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                              <ArticleIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
                              Source Articles ({term.sourceArticles?.length || 0})
                            </Typography>
                            <Paper
                              variant="outlined"
                              sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}
                            >
                              {term.sourceArticles?.map((source, idx) => (
                                <Box key={idx} sx={{ mb: 2 }}>
                                  <Typography variant="body2" fontWeight="medium">
                                    {source.articleTitle || 'Unknown Article'}
                                  </Typography>
                                  {source.chunkText && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        mt: 0.5,
                                        p: 1,
                                        bgcolor: 'grey.100',
                                        borderRadius: 1,
                                        fontSize: '0.8rem',
                                      }}
                                    >
                                      "...{source.chunkText.slice(0, 200)}..."
                                    </Typography>
                                  )}
                                </Box>
                              )) || (
                                <Typography variant="body2" color="text.secondary">
                                  No source articles recorded
                                </Typography>
                              )}
                            </Paper>
                          </Grid>

                          {/* Metadata */}
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              First seen: {formatTimestamp(term.firstSeen)} | Last seen:{' '}
                              {formatTimestamp(term.lastSeen)}
                              {term.confirmedBy && (
                                <>
                                  {' '}
                                  | Confirmed by: {term.confirmedBy} at{' '}
                                  {formatTimestamp(term.confirmedAt)}
                                </>
                              )}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredTerms.length === 0 && !loading && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No terms match your filters
          </Typography>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, term: null, action: null })}>
        <DialogTitle>
          Confirm {confirmDialog.action === 'generic' ? 'Generic' : 'Valid'} Term
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to mark "<strong>{confirmDialog.term?.term}</strong>" as{' '}
            {confirmDialog.action === 'generic' ? (
              <Chip label="Generic" color="error" size="small" />
            ) : (
              <Chip label="Valid Venue" color="success" size="small" />
            )}
            ?
          </Typography>
          {confirmDialog.action === 'generic' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This term will be filtered out from future extractions.
            </Alert>
          )}
          {confirmDialog.action === 'valid' && (
            <Alert severity="success" sx={{ mt: 2 }}>
              This term will be allowed in future extractions.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, term: null, action: null })}>
            Cancel
          </Button>
          <Button
            onClick={executeConfirmation}
            color={confirmDialog.action === 'generic' ? 'error' : 'success'}
            variant="contained"
            disabled={updating}
          >
            {updating ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
